import { throttle } from 'lodash-es';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import useResizeObserver from 'use-resize-observer';

interface UseSetTopListItemPositionProps {
  topItemListRef: MutableRefObject<HTMLDivElement | null>;
  virtuosoId: string;
}

/**
 * Virtuoso's list wrapper (virtuoso-item-list) has paddingTop to separate
 * from sticky header. When Virtuoso component TopListItem changes its height,
 * virtuoso-item-list doesn't update it's padding.
 * This hook, maintains that paddingTop update to date.
 */
export const useSetTopListItemPosition = ({
  topItemListRef,
  virtuosoId,
}: UseSetTopListItemPositionProps) => {
  const virtuosoScrollerRef = useRef<HTMLElement | null>(null);

  const virtuosoItemListSelector = useMemo(() => {
    return `[id="${virtuosoId}"] [data-testid="virtuoso-item-list"]`;
  }, [virtuosoId]);

  const updateVirtuosoListPaddingTop = useCallback(() => {
    requestAnimationFrame(() => {
      const topItemListEl = topItemListRef.current;
      const virtuosoItemListEl = document.querySelector(
        virtuosoItemListSelector
      ) as HTMLDivElement;

      if (virtuosoItemListEl && topItemListEl) {
        virtuosoItemListEl.style.paddingTop = `${topItemListEl.offsetHeight}px`;
      }
    });
  }, [topItemListRef, virtuosoItemListSelector]);

  const throttledScrollHandler = useMemo(
    () =>
      throttle((event: Event) => {
        if (!event.target || !topItemListRef.current) return;
        const scrollY = (event.target as HTMLElement).scrollTop as number;
        if (scrollY === 0 || scrollY < topItemListRef.current.offsetTop) {
          updateVirtuosoListPaddingTop();
        }
      }, 300),
    [topItemListRef, updateVirtuosoListPaddingTop]
  );

  useEffect(() => {
    const virtuosoScrollerEl = document.getElementById(
      virtuosoId
    ) as HTMLDivElement;

    if (!virtuosoScrollerEl) {
      return;
    }

    virtuosoScrollerRef.current = virtuosoScrollerEl;

    virtuosoScrollerEl.addEventListener('scroll', throttledScrollHandler);

    return () => {
      throttledScrollHandler.cancel();
      virtuosoScrollerEl.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [virtuosoId, throttledScrollHandler]);

  const throttledResizeHandler = useMemo(
    () =>
      throttle(() => {
        const topItemListEl = topItemListRef.current;
        if (!topItemListEl || !virtuosoScrollerRef.current) {
          return;
        }

        if (
          virtuosoScrollerRef.current.scrollTop === 0 ||
          virtuosoScrollerRef.current.scrollTop < topItemListEl.offsetTop
        ) {
          updateVirtuosoListPaddingTop();
        }
      }, 300),
    [topItemListRef, updateVirtuosoListPaddingTop]
  );

  // Apply paddingTop to virtuoso-item-list getting it from TopItemList height.
  useResizeObserver<HTMLDivElement>({
    ref: topItemListRef,
    onResize: throttledResizeHandler,
  });
};
